<template>
  <b-modal
    :active="true"
    :can-cancel="['escape', 'x', 'outside']"
    @close="onClose"
  >
    <div class="content">
      <b-table
        :data="jobTaskPreogress"
        :loading="isLoading"
        :sticky-header="true"
        :height="500"
      >
        <template slot-scope="props">
          <b-table-column field="taskName" :label="$t('shared.task')">
            {{ props.row.taskName }}
          </b-table-column>

          <b-table-column field="status" :label="$t('shared.status')">
            {{ props.row.status }}
          </b-table-column>

          <b-table-column
            field="completenessPercentage"
            :label="$t('shared.percent_complete')"
          >
            {{ props.row.completenessPercentage }}%
          </b-table-column>

          <b-table-column field="signedBy" :label="$t('shared.signed_off_by')">
            {{ props.row.signedBy }}
          </b-table-column>

          <b-table-column
            v-if="props.row.signedAt && props.row.workStatusCode === '2'"
            field="signedAt"
            :label="$t('shared.signed_off_at')"
          >
            {{
              `${new Date(props.row.signedAt).toLocaleDateString()} ${new Date(
                props.row.signedAt
              ).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}`
            }}
          </b-table-column>

          <b-table-column
            v-else
            field="signedAt"
            :label="$t('shared.signed_off_at')"
          >
            -
          </b-table-column>

          <b-table-column field="taskComment" :label="$t('comments')">
            {{ props.row.taskComment }}
          </b-table-column>

          <b-table-column
            field="_id"
            :label="$t('reopen')"
            v-if="userAllowedTools.includes('publisher')"
          >
            <b-button
              icon-right="backup-restore"
              type="is-primary"
              :disabled="props.row.workStatusCode !== '2' || !canEditCurrentJob"
              @click="reopenTask(props.row._id)"
            />
          </b-table-column>
        </template>

        <template #empty>
          <div class="has-text-centered empty-class">
            {{ $t('shared.no_records') }}
          </div>
        </template>
      </b-table>
      <footer class="footer-buttons">
        <b-button :label="$t('shared.close')" @click="onClose" />
      </footer>
    </div>
  </b-modal>
</template>

<script>
import OfflineMixin from '../../mixins/OfflineMixin';
import WORK_STATUS_CODE from '../../constants/workStatusCode';
import { GET_JOB_PROGRESS } from '../../store/jobs/actions/actionTypes';
import { GET_JOB_PROGRESS_TASKS } from '../../store/jobs/getters/getterTypes';

export default {
  name: 'progress-modal',

  props: {
    tool: {
      type: String,
    },
  },

  mixins: [OfflineMixin],

  data() {
    return {
      tableData: [],
      isLoading: true,
    };
  },

  computed: {
    job() {
      return this.$store.state.currentJob;
    },

    jobTasks() {
      return this.$store.state.tasks;
    },

    jobTaskPreogress() {
      return this.$store.getters[GET_JOB_PROGRESS_TASKS];
    },

    user() {
      console.log('user', this.$store.state.currentUser);
      return this.$store.state.currentUser;
    },
    userAllowedTools() {
      return this.$store.state.currentUserAllowedTools;
    },
    canEditCurrentJob() {
      return this.$store.getters.canEditJob(this.job, this.tool);
    },
  },

  watch: {
    jobTasks: {
      immediate: true,
      handler(newVal) {
        this.tableData = newVal.map((task) => ({
          _id: task._id,
          taskName: task.taskName,
          workStatusCode: task.workStatusCode,
          status:
            task.workStatusCode === WORK_STATUS_CODE.SIGN_OFF
              ? this.$t('shared.status_complete')
              : this.$t('shared.status_open'),
          completenessPercentage: task.completenessPercentage.toFixed(2),
          signedBy:
            task.workStatusCode === WORK_STATUS_CODE.SIGN_OFF
              ? task.signedBy
              : '-',
          signedAt:
            task.workStatusCode === WORK_STATUS_CODE.SIGN_OFF && task.signedAt
              ? `${new Date(task.signedAt).toLocaleDateString()} ${new Date(
                  task.signedAt
                ).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : '-',
          taskComment: task.taskComment ? task.taskComment : '-',
        }));
      },
    },
  },

  async mounted() {
    this.isLoading = true;
    const job = this.currentJob;
    try {
      await this.$store.dispatch('getCurrentUserProfile');
      await this.$store.dispatch(GET_JOB_PROGRESS, job._id);
    } catch (e) {
      console.error('Error fetching job progress:', e);
      this.$store.dispatch('setErrorMessage', e.message);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    onClose() {
      this.$emit('onClose');
    },

    async reopenTask(taskId) {
      if (await this.preventOfflineAction()) {
        return;
      }

      if (!this.canEditCurrentJob) {
        return;
      }

      try {
        this.isLoading = true;
        const job = this.currentJob;
        const task = await this.$store.dispatch('getTask', taskId);
        this.editTask = { ...task };
        this.editTask.workStatusCode = 1;
        this.editTask.signedBy = null;
        this.editTask.signedAt = null;
        const updatedTask = this.editTask;
        await this.$store.dispatch('updateTask', updatedTask);
        await this.$store.dispatch('reopenJob', job._id);
        await this.$store.dispatch('getJobTasks', {
          jobId: job._id,
        });
        await this.$store.dispatch('getJob', job._id);
        await this.$store.dispatch(GET_JOB_PROGRESS, job._id);
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.content {
  background: white;
  border-radius: 10px;
  padding: 20px;
  gap: 10px;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  justify-content: space-between;
  font-family: 'Siemens Sans';
}

.empty-class {
  min-height: 500px;
}

.footer-buttons {
  display: flex;
  justify-content: end;
  margin-top: 10px;
}
</style>
